import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Email,
  Phone,
  LocationOn
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const ImpressumPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, getLegalVariables } = useLanguage();

  // Get dynamic variables for legal content
  const legalVars = getLegalVariables();

  const handleBack = () => {
    // Check if user came from within the app
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      // Default to landing page
      navigate('/');
    }
  };

  // Helper function to render content with line breaks
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => (
      <Typography key={index} variant="body1" sx={{ mb: line.trim() === '' ? 2 : 1 }}>
        {line}
      </Typography>
    ));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: tokens.colors.background.default,
        py: 4
      }}
    >
      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{
            mb: 4,
            color: tokens.colors.primary.main,
            textTransform: 'none',
            fontWeight: tokens.typography.fontWeights.medium,
            '&:hover': {
              backgroundColor: `${tokens.colors.primary.main}10`
            }
          }}
        >
          {t('legal.common.backButton')}
        </Button>

        {/* Page Content */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: tokens.radius.xl,
            border: `1px solid ${tokens.colors.borders.light}`
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: 4
            }}
          >
            {t('legal.imprint.title')}
          </Typography>

          {/* TMG Information */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.tmgInfo.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.tmgInfo.content', legalVars))}
          </Box>

          {/* Contact */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.contact.title')}
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('legal.imprint.sections.contact.content', legalVars)}
            </Typography>
          </Box>

          {/* Responsible for Content */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.responsibleContent.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.responsibleContent.content', legalVars))}
          </Box>

          {/* Content Liability */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.contentLiability.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.contentLiability.content'))}
          </Box>

          {/* Link Liability */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.linkLiability.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.linkLiability.content'))}
          </Box>

          {/* Copyright */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.copyright.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.copyright.content'))}
          </Box>

          {/* Dispute Resolution */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.imprint.sections.disputeResolution.title')}
            </Typography>
            
            {renderContent(t('legal.imprint.sections.disputeResolution.content'))}
          </Box>

          {/* Last Updated */}
          <Box sx={{ 
            pt: 3, 
            borderTop: `1px solid ${tokens.colors.borders.light}`,
            textAlign: 'center'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: tokens.colors.text.secondary,
                fontStyle: 'italic'
              }}
            >
              {t('legal.common.lastUpdated', legalVars)}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ImpressumPage; 